import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { FormContainer } from 'components/layout';
import { Checkbox, ChipInput, SelectableCampaignsGrid } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { formatEntityStatus, toIdSelectionEntries } from 'utils';
import type { PinterestCampaignEntry } from '../api';
import usePinterestCampaignsData from '../hooks/usePinterestCampaignsData';

export const PINTEREST_CAMPAIGNS_LIMIT = 20;
const EMPTY: string[] = [];

const COLUMNS: GridColDef<PinterestCampaignEntry>[] = [
  { field: 'campaign_id', headerName: 'Campaign ID', width: 160 },
  { field: 'campaign_name', headerName: 'Campaign Name', width: 700 },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {formatEntityStatus(params.row.status)}
      </Typography>
    ),
  },
];

const ChooseCampaigns = memo(() => {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const selectedCampaigns = (useWatch({ name: 'selectedCampaigns' }) as string[] | undefined) ?? EMPTY;
  const filterByName = useWatch({ name: 'filterCampaignsByName' }) as string[] | undefined;
  const campaignsError = errors.selectedCampaigns?.message as string | undefined;
  const {
    setSelectedCampaigns: setSelectedCampaignsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
  } = useLocationParams();

  const { gridRows, isLoading, isError, errorMessage } = usePinterestCampaignsData();

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      setValue('selectedCampaigns', ids, { shouldValidate: true, shouldDirty: true });
      setValue('selectedAdGroups', [], { shouldValidate: true });
      setSelectedAdSetsParam([]);
    },
    [setValue, setSelectedAdSetsParam],
  );

  const getRowId = useCallback((row: PinterestCampaignEntry) => row.campaign_id, []);

  useEffect(() => {
    setSelectedCampaignsParam(toIdSelectionEntries(selectedCampaigns));
  }, [selectedCampaigns, setSelectedCampaignsParam]);

  useEffect(() => {
    setFilterCampaignsByNameParam(filterByName ?? []);
  }, [filterByName, setFilterCampaignsByNameParam]);

  useEffect(() => {
    void trigger('selectedCampaigns');
  }, [trigger]);

  useEffect(() => {
    if (!isError) return;

    datadogActions.trackDatadogError(new Error(errorMessage ?? 'Failed to load campaigns'), {
      source: 'pinterest_choose_campaigns_query',
    });
  }, [isError, errorMessage]);

  return (
    <FormContainer
      title="Choose Campaigns"
      error={campaignsError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ flex: 1, maxWidth: '50%' }}>
          <ChipInput name="filterCampaignsByName" inputLabel="Enter campaign name" />
        </Box>
        <Checkbox
          dataTestId="selectedCampaignsOnly"
          name="selectedCampaignsOnly"
          label="Selected only"
          controlLabelSx={{ whiteSpace: 'nowrap' }}
        />
      </Box>
      {campaignsError ? (
        <Typography variant="caption" color="error" role="alert">
          {campaignsError}
        </Typography>
      ) : null}

      {isError ? (
        <Alert severity="error" sx={{ width: '100%' }} data-testid="pinterest-campaigns-error">
          {errorMessage}
        </Alert>
      ) : (
        <SelectableCampaignsGrid
          rows={gridRows}
          columns={COLUMNS}
          getRowId={getRowId}
          selectedIds={selectedCampaigns}
          onSelectionChange={handleSelectionChange}
          limit={PINTEREST_CAMPAIGNS_LIMIT}
          limitMessage={`Up to ${PINTEREST_CAMPAIGNS_LIMIT} campaigns can be selected`}
          isLoading={isLoading}
        />
      )}
    </FormContainer>
  );
});

ChooseCampaigns.displayName = 'ChooseCampaigns';

export default ChooseCampaigns;
